import { cn } from '@/lib/utils';

interface ScoreDialProps {
  score: number;
  size?: number;
  label?: string;
  className?: string;
  colorClass?: string;
}

/** A minimalist circular score gauge (0-100) rendered with SVG. */
export function ScoreDial({
  score,
  size = 140,
  label,
  className,
  colorClass = 'text-primary',
}: ScoreDialProps) {
  const stroke = size * 0.08;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score)) / 100;
  const offset = c * (1 - pct);

  return (
    <div
      className={cn('relative inline-flex items-center justify-center', className)}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          className="text-border/50"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          className={cn(colorClass, 'transition-all duration-700 ease-out')}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className={cn('font-mono font-bold tabular-nums leading-none', colorClass)}
          style={{ fontSize: size * 0.3 }}
        >
          {Math.round(score)}
        </span>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
          {label ?? '/ 100'}
        </span>
      </div>
    </div>
  );
}
